import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import DeleteIcon from '@mui/icons-material/Delete';
import CoreHttpHandler from 'http/services/CoreHttpHandler';

const VALID_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const VALID_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const SIZE_LIMIT_BYTES = 10000000;

const useStyles = makeStyles(theme => ({
	uploadSection: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(3),
		border: `2px dashed ${theme.palette.divider}`,
		borderRadius: 12,
		textAlign: 'center',
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		'&:hover': {
			borderColor: theme.palette.primary.main,
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)'
		}
	},
	dragActive: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(25, 118, 210, 0.08)',
		transform: 'scale(1.01)'
	},
	uploadIcon: {
		fontSize: 42,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1)
	},
	uploadText: {
		color: theme.palette.text.secondary,
		fontSize: 14
	},
	uploadHint: {
		color: theme.palette.text.disabled,
		fontSize: 12,
		marginTop: theme.spacing(0.5)
	},
	filePreview: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1.5, 2),
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(25, 118, 210, 0.06)',
		borderRadius: 10,
		border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`
	},
	previewHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	checkIcon: {
		fontSize: 20,
		color: theme.palette.success.main
	},
	fileName: {
		flex: 1,
		fontSize: 13,
		fontWeight: 600,
		color: theme.palette.text.primary,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	imagePreview: {
		width: '100%',
		marginTop: theme.spacing(1),
		maxHeight: 180,
		objectFit: 'contain',
		borderRadius: 8,
		display: 'block',
		background: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)'
	},
	iconButton: {
		padding: 6,
		color: theme.palette.text.secondary
	},
	errorText: {
		color: theme.palette.error.main,
		fontSize: 12,
		marginTop: theme.spacing(0.5)
	}
}));

/**
 * BannerImageUploader - drag & drop image uploader matching the call-chatbot
 * "Add Node" audio uploader UI. Uploads via `content/upload` and reports the
 * hosted link back through `onChange(url)`. Fully controlled by `value` (URL).
 */
function BannerImageUploader({ value, onChange, onUploadingChange }) {
	const classes = useStyles();
	const inputRef = useRef();

	const [dragActive, setDragActive] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState(null);
	const [copied, setCopied] = useState(false);
	const [fileName, setFileName] = useState('');

	const setUploadingState = next => {
		setUploading(next);
		if (onUploadingChange) onUploadingChange(next);
	};

	const validateExtension = name => {
		const lower = (name || '').toLowerCase();
		return VALID_EXTENSIONS.some(ext => lower.endsWith(ext));
	};

	const validateMimeType = type => {
		const lower = (type || '').toLowerCase();
		return VALID_MIME_TYPES.includes(lower) || lower.startsWith('image/');
	};

	const processFile = file => {
		if (!file) return;
		setError(null);

		if (!validateExtension(file.name) && !validateMimeType(file.type)) {
			setError('Only image files are allowed (JPG, PNG, GIF, WEBP).');
			return;
		}
		if (file.size >= SIZE_LIMIT_BYTES) {
			setError('File size limit exceeded. Please select a file less than 10 MB.');
			return;
		}

		const formData = new FormData();
		const safeName = file.name.replace(/\s/g, '');
		formData.append('file', file, `${new Date().getTime()}_${safeName}`);

		setUploadingState(true);
		CoreHttpHandler.request(
			'content',
			'upload',
			{ params: formData },
			response => {
				setUploadingState(false);
				setFileName(file.name);
				if (onChange) onChange(response.data.data.link);
			},
			() => {
				setUploadingState(false);
				setError('Upload failed. Please try again.');
			}
		);
	};

	const handleDrag = e => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			const rect = e.currentTarget.getBoundingClientRect();
			if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
				setDragActive(false);
			}
		}
	};

	const handleDrop = e => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			processFile(e.dataTransfer.files[0]);
		}
	};

	const handleFileChange = e => {
		if (e.target.files && e.target.files[0]) {
			processFile(e.target.files[0]);
		}
		e.target.value = '';
	};

	const handleRemove = () => {
		setError(null);
		setFileName('');
		if (onChange) onChange('');
	};

	const handleCopy = () => {
		if (!value) return;
		navigator.clipboard
			.writeText(value)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			})
			.catch(() => {});
	};

	const displayName = fileName || (value ? value.split('/').pop() : '');

	return (
		<div>
			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>

			{!value ? (
				<div
					className={`${classes.uploadSection} ${dragActive ? classes.dragActive : ''}`}
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
					onClick={() => !uploading && inputRef.current && inputRef.current.click()}
				>
					{uploading ? (
						<div className="flex flex-col items-center">
							<CircularProgress size={32} />
							<Typography className={classes.uploadText} style={{ marginTop: 8 }}>
								Uploading...
							</Typography>
						</div>
					) : (
						<>
							<Icon className={classes.uploadIcon}>image</Icon>
							<Typography className={classes.uploadText}>
								{dragActive ? 'Drop your image here' : 'Click to upload image'}
							</Typography>
							<Typography className={classes.uploadHint}>
								or drag &amp; drop • JPG / PNG / GIF / WEBP • Max 10MB
							</Typography>
						</>
					)}
				</div>
			) : (
				<div className={classes.filePreview}>
					<div className={classes.previewHeader}>
						<Icon className={classes.checkIcon}>check_circle</Icon>
						<Typography className={classes.fileName} title={displayName}>
							{displayName}
						</Typography>
						<Tooltip title={copied ? 'Copied!' : value} arrow PopperProps={{ style: { zIndex: 13000 } }}>
							<IconButton className={classes.iconButton} size="small" onClick={handleCopy}>
								{copied ? (
									<DoneIcon style={{ fontSize: 18 }} />
								) : (
									<ContentCopyIcon style={{ fontSize: 18 }} />
								)}
							</IconButton>
						</Tooltip>
						<Tooltip title="Remove" arrow PopperProps={{ style: { zIndex: 13000 } }}>
							<DeleteIcon style={{ cursor: 'pointer' }} fontSize="large" onClick={handleRemove} />
						</Tooltip>
					</div>
					<img
						className={classes.imagePreview}
						src={value}
						alt={displayName || 'banner'}
						onError={e => {
							e.target.style.display = 'none';
						}}
					/>
				</div>
			)}

			{error ? <div className={classes.errorText}>{error}</div> : null}
		</div>
	);
}

export default BannerImageUploader;
